// client/src/pages/SupervisorDashboardPage.js
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Container,
  Alert,
  Table,
  Form,
  Button,
  Card,
  Collapse,
  Row,
  Col,
  Modal
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { getAuthHeaders } from '../utils/authHelpers';
import TaskGroup from '../components/TaskGroup';
import '../styles/OrderList.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

export default function SupervisorDashboardPage() {
  const {
    user,
    allOrders,
    loading,
    error,
    submissionMessage,
    setSubmissionMessage,
    orderTaskStates,
    setOrderTaskStates,
    getTasksForStage,
    stageMap,
    formatDate,
    groupTasksForDisplay,
    fetchOrdersForRole
  } = useDashboardData();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('All');
  const [expandedRows, setExpandedRows] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const topScrollRef = useRef(null);
  const topInnerRef = useRef(null);
  const tableScrollRef = useRef(null);

  /* -------------------- Filter Helpers -------------------- */
  const orderTypes = useMemo(() => {
    const types = new Set(allOrders.map(o => o.orderType).filter(Boolean));
    return ['All', ...Array.from(types).sort()];
  }, [allOrders]);

  const filteredOrders = useMemo(() => {
    return allOrders.filter(o => {
      const s = searchTerm.toLowerCase();
      const matchesSearch = 
        o.orderId.toLowerCase().includes(s) ||
        o.customerName?.toLowerCase().includes(s) ||
        o.invoiceNumber?.toLowerCase().includes(s) ||
        o.dealerName?.toLowerCase().includes(s);
      
      const matchesType = selectedOrderType === 'All' || o.orderType === selectedOrderType;
      return matchesSearch && matchesType;
    });
  }, [allOrders, searchTerm, selectedOrderType]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedOrderType('All');
    setExpandedRows({});
  };

  /* -------------------- Task Helpers -------------------- */
  const getTasksForOrder = useCallback(
    (order) => getTasksForStage(order.currentStage) || [],
    [getTasksForStage]
  );

  const initialTaskStates = useMemo(() => {
    const map = {};
    allOrders.forEach(order => {
      const stageKey = stageMap[order.currentStage];
      const stageData = order[stageKey] || {};
      const snapshot = {};

      getTasksForOrder(order).forEach(task => {
        if (task.type === 'checkbox') {
          snapshot[task.id] = stageData[task.id] || false;
        } else if (task.type === 'date') {
          snapshot[task.id] = stageData[task.id]
            ? new Date(stageData[task.id]).toISOString().split('T')[0]
            : '';
        }
      });
      map[order.orderId] = snapshot;
    });
    return map;
  }, [allOrders, stageMap, getTasksForOrder]);

  useEffect(() => {
    if (Object.keys(orderTaskStates).length === 0 && Object.keys(initialTaskStates).length > 0) {
      setOrderTaskStates(initialTaskStates);
    }
  }, [initialTaskStates, orderTaskStates, setOrderTaskStates]);

  useEffect(() => {
    if (!submissionMessage.message) return;
    const timer = setTimeout(() => {
      setSubmissionMessage({ message: '', type: '' });
    }, 3000);
    return () => clearTimeout(timer);
  }, [submissionMessage.message, setSubmissionMessage]);

  /* -------------------- Scroll Sync Logic -------------------- */
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableScroll = tableScrollRef.current;
    const topInner = topInnerRef.current;
    if (!topScroll || !tableScroll || !topInner) return;

    const syncWidths = () => {
      const tableElement = tableScroll.querySelector('table');
      if (tableElement) topInner.style.width = `${tableElement.scrollWidth}px`;
    };

    const handleTopScroll = () => { tableScroll.scrollLeft = topScroll.scrollLeft; };
    const handleTableScroll = () => { topScroll.scrollLeft = tableScroll.scrollLeft; };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);
    syncWidths();
    const ro = new ResizeObserver(syncWidths);
    ro.observe(tableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      ro.disconnect();
    };
  }, [filteredOrders]);

  const handleTaskChange = useCallback((orderId, taskId, value) => {
    setOrderTaskStates(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], [taskId]: value }
    }));
  }, [setOrderTaskStates]);

  const isOrderDirty = useCallback((order) => {
    const current = orderTaskStates[order.orderId] || {};
    const initial = initialTaskStates[order.orderId] || {};
    return Object.keys(current).some(key => current[key] !== initial[key]);
  }, [orderTaskStates, initialTaskStates]);

  const TaskInputRow = ({ order, task, currentOrderTaskState, handleTaskChange, loading, alignDesktop = 'start' }) => {
    const flexClass = alignDesktop === 'center' ? "d-flex justify-content-center" : "d-flex justify-content-center justify-content-md-start";
    const textClass = alignDesktop === 'center' ? "text-center" : "text-center text-md-start";
    return (
      <div className="mb-2 w-100">
        {task.type === 'checkbox' ? (
          <div className={flexClass}>
            <Form.Check type="checkbox" id={`task-${order.orderId}-${task.id}`} className="mb-0">
              <Form.Check.Input type="checkbox" checked={!!currentOrderTaskState[task.id]} onChange={(e) => handleTaskChange(order.orderId, task.id, e.target.checked)} disabled={loading || (task.editableBy && !task.editableBy.includes(user?.role))} />
              <Form.Check.Label className="ms-2">{task.label}</Form.Check.Label>
            </Form.Check>
          </div>
        ) : (
          <Form.Group controlId={`task-${order.orderId}-${task.id}`} className="mb-0 w-100">
            <Form.Label className={`mb-1 d-block ${textClass}`}>{task.label} {task.required && <span className="text-danger">*</span>}</Form.Label>
            <div className={flexClass}>
              <Form.Control type="date" value={currentOrderTaskState[task.id] ?? ''} onChange={(e) => handleTaskChange(order.orderId, task.id, e.target.value)} disabled={loading || (task.editableBy && !task.editableBy.includes(user?.role))} className="w-auto" />
            </div>
          </Form.Group>
        )}
      </div>
    );
  };

    // Triggered by the "Save My Changes" button
    const handleShowConfirm = () => {
    const dirtyCount = filteredOrders.filter(isOrderDirty).length;
    if (dirtyCount > 0) {
        setShowConfirmModal(true);
    } else {
        setSubmissionMessage({ message: 'No changes to save.', type: 'info' });
    }
    };

    // Triggered by the "Confirm" button inside the Modal
    const handleConfirmSave = async () => {
    setShowConfirmModal(false); // Close modal immediately
    const dirtyOrders = filteredOrders.filter(isOrderDirty);

    try {
        for (const order of dirtyOrders) {
        const stageKey = stageMap[order.currentStage];
        await fetch(`${ORDERS_API_URL}/${order.orderId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
            [stageKey]: { 
                ...(order[stageKey] || {}), 
                ...orderTaskStates[order.orderId] 
            },
            updatedAt: new Date()
            })
        });
        }
        await fetchOrdersForRole(); 
        setSubmissionMessage({ message: 'Tasks saved successfully.', type: 'success' });
    } catch (err) {
        setSubmissionMessage({ message: 'Failed to save tasks.', type: 'danger' });
    }
    };

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Supervisor') return <Navigate to="/dashboard" replace />;

  return (
    <Container fluid className="bg-light min-vh-100 py-4">
      <Container>
        <Card className="mb-4 shadow-sm text-center">
          <Card.Body>
            <h2>Welcome, {user.username}.</h2>
            <p className="text-muted mb-0">Manufacturing Task Dashboard</p>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {submissionMessage.message && <Alert variant={submissionMessage.type}>{submissionMessage.message}</Alert>}

        <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
          <div className="d-flex gap-2 flex-grow-1" style={{ maxWidth: '600px' }}>
            <Form.Control
              placeholder="Search by ID, Customer, Invoice, or Dealer..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Form.Select 
              style={{ maxWidth: '200px' }}
              value={selectedOrderType}
              onChange={e => setSelectedOrderType(e.target.value)}
            >
              {orderTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
            <button type="button" onClick={handleClearFilters} className="btn btn-outline-secondary">
                Clear
            </button>
          </div>

          <Button
            variant={filteredOrders.every(o => !isOrderDirty(o)) ? 'secondary' : 'success'}
            disabled={loading || filteredOrders.every(o => !isOrderDirty(o))}
            onClick={handleShowConfirm}
            >
            {loading ? 'Saving...' : 'Save My Changes'}
          </Button>
        </div>

        <div className="table-scroll-top-wrapper">
          <div className="top-scroll" ref={topScrollRef} style={{ overflowX: 'auto', height: '15px', visibility: loading ? 'hidden' : 'visible' }}>
            <div className="top-scroll-inner" ref={topInnerRef} style={{ height: '1px' }} />
          </div>

          <div className="table-responsive" ref={tableScrollRef}>
            <Table bordered hover className="shadow-sm rounded order-list-table" style={{ minWidth: '1200px' }}>
              <thead className="table-dark">
                <tr>
                  <th>Order ID</th>
                  <th>Invoice Number</th>
                  <th>Order Type</th>
                  <th>Dealer Name</th>
                  <th>Customer Name</th>
                  <th>Custom Work</th>
                  <th>Shipping Method</th>
                  <th>Order Date</th>
                  <th style={{ width: 150 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0 text-muted">Loading your production tasks...</p>
                    </td>
                  </tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      No matching manufacturing orders found.
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map(order => {
                    const tasksForCurrentStage = getTasksForOrder(order);
                    const currentOrderTaskState = orderTaskStates[order.orderId] || {};

                    return (
                      <React.Fragment key={order.orderId}>
                        <tr>
                          <td>{order.orderId}</td>
                          <td>{order.invoiceNumber || 'N/A'}</td>
                          <td>{order.orderType || 'N/A'}</td>
                          <td>{order.dealerName || 'N/A'}</td>
                          <td>{order.customerName || 'N/A'}</td>
                          <td>{order.customWork || 'None'}</td>
                          <td>{order.shippingMethod || 'N/A'}</td>
                          <td>{formatDate(order.orderDate, false)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setExpandedRows(p => ({ ...p, [order.orderId]: !p[order.orderId] }))}
                            >
                              {expandedRows[order.orderId] ? 'Hide Tasks' : 'View Tasks'}
                            </Button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={9} className="p-0">
                            <Collapse in={!!expandedRows[order.orderId]}>
                              <div className="p-3 bg-light border-top">
                                {tasksForCurrentStage.length === 0 ? (
                                  <span className="text-muted ms-2">No tasks defined for this stage.</span>
                                ) : (
                                  <Row className="gx-4">
                                    {(() => {
                                      const grouped = groupTasksForDisplay(tasksForCurrentStage);
                                      const seamstress = grouped.find(g => g.group === 'Seamstress Tasks');
                                      const getS = (name) => seamstress?.subtasks?.find(s => s.subgroup === name) || { tasks: [] };

                                      return (
                                        <>
                                          <Col md={4}>
                                            {grouped.filter(g => ['Manager Tasks', 'Welder Tasks', 'Assembler Tasks'].includes(g.group)).map((group, idx) => (
                                              <TaskGroup key={idx} group={group} order={order} currentOrderTaskState={currentOrderTaskState} handleTaskChange={handleTaskChange} loading={loading} TaskInputRow={TaskInputRow} />
                                            ))}
                                          </Col>
                                          <Col md={4}>
                                            <TaskGroup group={{ group: 'Seamstress Tasks', subtasks: [getS('Drawing'), getS('Cutting')] }} order={order} currentOrderTaskState={currentOrderTaskState} handleTaskChange={handleTaskChange} loading={loading} TaskInputRow={TaskInputRow} />
                                          </Col>
                                          <Col md={4}>
                                            <TaskGroup group={{ group: 'Seamstress Tasks', subtasks: [getS('Sewing'), getS('Completing & Packing')] }} order={order} currentOrderTaskState={currentOrderTaskState} handleTaskChange={handleTaskChange} loading={loading} TaskInputRow={TaskInputRow} />
                                          </Col>
                                        </>
                                      );
                                    })()}
                                  </Row>
                                )}
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <Modal 
            show={showConfirmModal} 
            onHide={() => setShowConfirmModal(false)} 
            centered
            backdrop="static" // Prevents closing by clicking outside during save
            >
            <Modal.Header closeButton className="bg-success text-white">
                <Modal.Title>Confirm Save</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4">
                <div className="text-center">
                <i className="bi bi-exclamation-circle text-warning" style={{ fontSize: '2rem' }}></i>
                <p className="mt-3 mb-0">
                    You have modified tasks for <strong>{filteredOrders.filter(isOrderDirty).length}</strong> order(s).
                </p>
                <p className="text-muted small">Are you sure you want to update these records?</p>
                </div>
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                Cancel
                </Button>
                <Button variant="success" onClick={handleConfirmSave}>
                Confirm & Save
                </Button>
            </Modal.Footer>
        </Modal>
      </Container>
    </Container>
  );
}